// src/pages/personal/settings/PromotionsDiscountsSection/ClientStatusRules.tsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';
import { TEXT_STYLES } from '@/theme/typography';
import { COMPONENT_STYLES } from '@/theme/components';
import useBusinessCurrency from '@/hooks/useBusinessCurrency';
import { Card, Flex } from '@/components/ui/Layout';
import { Table, TableColumn } from '@/components/ui/Table/Table';
import { useResponsive } from '@/hooks/useResponsive';

interface ClientStatusRulesProps {
  clientStatusRules: {
    [status: string]: {
      discount: {
        type: 'percentage' | 'fixed_amount';
        value: number;
      };
      loyalty_bonus: {
        type: 'percentage' | 'fixed_amount';
        value: number;
      };
    };
  };
  onChange: (data: any) => void;
}

// Определим тип для строки данных таблицы
interface StatusTableRow {
  status: string;
  statusLabel: string;
  discountType: 'percentage' | 'fixed_amount';
  discountValue: number;
  loyaltyBonusType: 'percentage' | 'fixed_amount';
  loyaltyBonusValue: number;
}

const ClientStatusRules: React.FC<ClientStatusRulesProps> = ({ clientStatusRules, onChange }) => {
  // Указываем используемые пространства имен
  const { t } = useTranslation(['promotions', 'client_statuses', 'common']);
  const { currencySymbol } = useBusinessCurrency();
  const { isMobile } = useResponsive();
  
  // Статусы клиентов
  const clientStatuses = [
    { value: 'cold', label: t('client_statuses:cold') },
    { value: 'rare', label: t('client_statuses:rare') },
    { value: 'new', label: t('client_statuses:new') },
    { value: 'regular', label: t('client_statuses:regular') },
    { value: 'loyal', label: t('client_statuses:loyal') },
    { value: 'lost', label: t('client_statuses:lost') }
  ];

  // Обработчик изменения типа скидки
  const handleDiscountTypeChange = (status: string, type: 'percentage' | 'fixed_amount') => {
    const updatedRule = {
      ...clientStatusRules[status],
      discount: {
        ...clientStatusRules[status].discount,
        type
      }
    };
    
    onChange({ [status]: updatedRule });
  };

  // Обработчик изменения значения скидки
  const handleDiscountValueChange = (status: string, value: number) => {
    const updatedRule = {
      ...clientStatusRules[status],
      discount: {
        ...clientStatusRules[status].discount,
        value
      }
    };
    
    onChange({ [status]: updatedRule });
  };

  // Обработчик изменения типа бонуса лояльности
  const handleLoyaltyBonusTypeChange = (status: string, type: 'percentage' | 'fixed_amount') => {
    const updatedRule = {
      ...clientStatusRules[status],
      loyalty_bonus: {
        ...clientStatusRules[status].loyalty_bonus,
        type
      }
    };
    
    onChange({ [status]: updatedRule });
  };

  // Обработчик изменения значения бонуса лояльности
  const handleLoyaltyBonusValueChange = (status: string, value: number) => {
    const updatedRule = {
      ...clientStatusRules[status],
      loyalty_bonus: {
        ...clientStatusRules[status].loyalty_bonus,
        value
      }
    };
    
    onChange({ [status]: updatedRule });
  };

  // Подготавливаем данные для Table компонента
  const tableData: StatusTableRow[] = useMemo(() => clientStatuses.map(status => {
    const rule = clientStatusRules[status.value] || {
      discount: { type: 'percentage', value: 0 },
      loyalty_bonus: { type: 'percentage', value: 0 }
    };
    
    return {
      status: status.value,
      statusLabel: status.label,
      discountType: rule.discount.type,
      discountValue: rule.discount.value,
      loyaltyBonusType: rule.loyalty_bonus.type,
      loyaltyBonusValue: rule.loyalty_bonus.value
    };
  }), [clientStatusRules, clientStatuses, t]);

  // Определяем колонки для Table компонента
  const columns: TableColumn<StatusTableRow>[] = useMemo(() => [
    { 
      key: 'statusLabel', 
      title: t('promotions:promotions.client_status'), 
      width: 'md',
      render: (item) => (
        <span className="font-medium">{item.statusLabel}</span>
      ),
      mobilePriority: 0 // Высший приоритет на мобильных
    },
    { 
      key: 'discountType', 
      title: t('promotions:promotions.discount_type'), 
      width: 'md',
      render: (item) => (
        <select
          value={item.discountType}
          onChange={(e) => handleDiscountTypeChange(item.status, e.target.value as 'percentage' | 'fixed_amount')}
          className={COMPONENT_STYLES.form.input}
        >
          <option value="percentage">{t('promotions:promotions.percentage')}</option>
          <option value="fixed_amount">{t('promotions:promotions.fixed_amount')}</option>
        </select>
      ),
      mobilePriority: 1
    },
    { 
      key: 'discountValue', 
      title: t('promotions:promotions.discount_value'), 
      width: 'md',
      render: (item) => (
        <Flex align="center">
          <input
            type="number"
            min="0"
            value={item.discountValue}
            onChange={(e) => handleDiscountValueChange(item.status, parseFloat(e.target.value))}
            className={COMPONENT_STYLES.form.input}
          />
          <span className="ml-2">
            {item.discountType === 'percentage' ? '%' : currencySymbol}
          </span>
        </Flex>
      ),
      mobilePriority: 2
    },
    { 
      key: 'loyaltyBonusType', 
      title: t('promotions:promotions.loyalty_bonus_type'), 
      width: 'md',
      render: (item) => (
        <select
          value={item.loyaltyBonusType}
          onChange={(e) => handleLoyaltyBonusTypeChange(item.status, e.target.value as 'percentage' | 'fixed_amount')}
          className={COMPONENT_STYLES.form.input}
        >
          <option value="percentage">{t('promotions:promotions.percentage')}</option>
          <option value="fixed_amount">{t('promotions:promotions.fixed_amount')}</option>
        </select>
      ),
      mobilePriority: 3
    },
    { 
      key: 'loyaltyBonusValue', 
      title: t('promotions:promotions.loyalty_bonus_value'), 
      width: 'md',
      render: (item) => (
        <Flex align="center">
          <input
            type="number"
            min="0"
            value={item.loyaltyBonusValue}
            onChange={(e) => handleLoyaltyBonusValueChange(item.status, parseFloat(e.target.value))}
            className={COMPONENT_STYLES.form.input}
          />
          <span className="ml-2">
            {item.loyaltyBonusType === 'percentage' ? '%' : currencySymbol}
          </span>
        </Flex>
      ),
      mobilePriority: 4
    }
  ], [t, currencySymbol, handleDiscountTypeChange, handleDiscountValueChange, handleLoyaltyBonusTypeChange, handleLoyaltyBonusValueChange]);

  // Компонент для рендеринга карточки статуса клиента (для мобильной версии)
  const renderStatusCard = (item: StatusTableRow) => {
    return (
      <Card key={item.status} className="mb-4 border border-gray-200" padding="sm">
        <div className="mb-3 border-b border-gray-100 pb-2">
          <span className={`${TEXT_STYLES.h3} font-medium`}>{item.statusLabel}</span>
        </div>
        
        <div className="space-y-4">
          {/* Скидка */}
          <div>
            <div className="mb-2 text-sm font-medium text-gray-600">
              {t('promotions:promotions.discount')}
            </div>
            <div className="flex items-center">
              <select
                value={item.discountType}
                onChange={(e) => handleDiscountTypeChange(item.status, e.target.value as 'percentage' | 'fixed_amount')}
                className={`${COMPONENT_STYLES.form.input} mr-2 flex-grow`}
              >
                <option value="percentage">{t('promotions:promotions.percentage')}</option>
                <option value="fixed_amount">{t('promotions:promotions.fixed_amount')}</option>
              </select>
              <div className="flex items-center w-1/2">
                <input
                  type="number"
                  min="0"
                  value={item.discountValue}
                  onChange={(e) => handleDiscountValueChange(item.status, parseFloat(e.target.value))}
                  className={COMPONENT_STYLES.form.input}
                />
                <span className="ml-2">
                  {item.discountType === 'percentage' ? '%' : currencySymbol}
                </span>
              </div>
            </div>
          </div>
          
          {/* Бонус лояльности */}
          <div>
            <div className="mb-2 text-sm font-medium text-gray-600">
              {t('promotions:promotions.loyalty_bonus')}
            </div>
            <div className="flex items-center">
              <select
                value={item.loyaltyBonusType}
                onChange={(e) => handleLoyaltyBonusTypeChange(item.status, e.target.value as 'percentage' | 'fixed_amount')}
                className={`${COMPONENT_STYLES.form.input} mr-2 flex-grow`}
              >
                <option value="percentage">{t('promotions:promotions.percentage')}</option>
                <option value="fixed_amount">{t('promotions:promotions.fixed_amount')}</option>
              </select>
              <div className="flex items-center w-1/2">
                <input
                  type="number"
                  min="0"
                  value={item.loyaltyBonusValue}
                  onChange={(e) => handleLoyaltyBonusValueChange(item.status, parseFloat(e.target.value))}
                  className={COMPONENT_STYLES.form.input}
                />
                <span className="ml-2">
                  {item.loyaltyBonusType === 'percentage' ? '%' : currencySymbol}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Card 
      padding={isMobile ? "sm" : "md"} 
      className="border border-gray-200"
    >
      <Flex align="center" gap="xs" className="mb-4">
        <Users className={`w-5 h-5 text-primary`} />
        <h3 className={TEXT_STYLES.h3}>{t('promotions:promotions.client_status_rules')}</h3>
      </Flex>
      
      <p className={`mb-4 text-sm text-gray-600`}>
        {t('promotions:promotions.client_status_rules_description')}
      </p>
      
      {isMobile ? (
        // Мобильный вариант - карточки
        <div>
          {tableData.map(renderStatusCard)}
        </div>
      ) : (
        // Десктопный вариант - таблица
        <Table
          data={tableData}
          columns={columns}
          scrollable={false}
          keyExtractor={(item) => item.status}
          responsive={true}
          mobileBehavior="cards" // Указываем поведение на мобильной версии
        />
      )}
    </Card>
  );
};

export default React.memo(ClientStatusRules);